<template>
	<v-card class="position-relative" max-height="800px">
		<v-overlay
			:model-value="initialLoading"
			persistent
			contained
			absolute
			class="d-flex align-center justify-center position-absolute"
		>
			<v-progress-circular indeterminate color="primary" />
		</v-overlay>

		<v-text-field
			v-model="searchKey"
			prepend-inner-icon="mdi-magnify"
			label="Search data"
			density="comfortable"
			clearable
			class="paper-search mx-4 mt-4"
			hide-details
			max-width="280px"
			:loading="searchLoading"
		/>

		<v-card class="overflow-auto" max-height="760px">
			<v-treeview
				ref="vTreeview"
				:items="treeViewData"
				item-title="name"
				item-value="id"
				open-on-click
				:opened="openNodes"
				:load-children="loadChildren"
				@click:select="selectNode"
			>
				<template #prepend="{ item }">
					<v-icon>
						{{ !item.isDataPoint ? "mdi-file-tree" : "mdi-text-box-search" }}
					</v-icon>
				</template>
				<template #title="{ item }">
					<span
						:class="{ 'selected-node': item.id === selectedNodeId }"
						:style="{
							fontWeight: item.children ? 'bold' : 'normal',
						}"
					>
						{{ item.name }}
					</span>
				</template>
			</v-treeview>
		</v-card>
	</v-card>
</template>

<script>
import { VTreeview } from "vuetify/labs/VTreeview";
import { mapActions } from "pinia";
import { useMainStore } from "@/store";
import _ from "lodash";
import createCompositeKeyUtils from "../../compositeKeyUtils";
import { v4 as uuidv4, validate as uuidValidate } from "uuid";

export default {
	name: "DactylTree",
	components: {
		VTreeview,
	},

	props: {
		sdeUuid: {
			type: String,
			default: "",
		},
		dataPointUuid: {
			type: String,
			default: "",
		},
	},
	emits: ["select:node"],

	data() {
		return {
			dactylTree: {},
			openNodes: [],
			parentNodeMap: {},
			searchKey: "",
			treeViewData: [],
			flattenedTreeData: [],
			fullTreeData: [],
			initialLoading: false,
			searchLoading: false,
			selectedNodeId: null,
			pendingDataPointUuid: null,
		};
	},

	watch: {
		async searchKey(val) {
			if (val?.length > 2) {
				this.searchLoading = true;

				const { visibleNodeIds, matchingInfo } = this.search(
					this.flattenedTreeData,
					this.searchKey,
				);

				const nodesToOpen = new Set();
				for (const nodeId of visibleNodeIds) {
					if (matchingInfo.nodesWithMatchingChildren.has(nodeId)) {
						nodesToOpen.add(nodeId);
					}

					if (!matchingInfo.directMatches.has(nodeId)) {
						nodesToOpen.add(nodeId);
					}
				}

				this.openNodes = Array.from(nodesToOpen);
				this.treeViewData = [];
				await this.buildVisibleTree(
					this.fullTreeData,
					visibleNodeIds,
					matchingInfo,
					this.treeViewData,
				);

				this.searchLoading = false;
			} else {
				this.treeViewData = [
					{
						...this.fullTreeData[0],
						children: [],
					},
				];
				this.openNodes = [];
			}
		},
		async sdeUuid(newVal) {
			if (newVal) {
				this.initialLoading = true;
				this.treeViewData = [];
				await this.getSdeData();
				this.buildNodeParentMap(this.fullTreeData);

				if (this.pendingDataPointUuid) {
					this.searchKey = this.pendingDataPointUuid;
					this.selectNodeByDataPointUuid(this.pendingDataPointUuid);
					this.pendingDataPointUuid = null;
				} else {
					this.searchKey = "";
					this.$emit("select:node", {});
				}
				this.initialLoading = false;
			}
		},
		dataPointUuid(newVal) {
			if (newVal && uuidValidate(newVal)) {
				if (this.initialLoading) {
					this.pendingDataPointUuid = newVal;
				} else {
					this.searchKey = newVal;
					this.selectNodeByDataPointUuid(newVal);
				}
			}
		},
	},

	async mounted() {
		this.initialLoading = true;
		await this.getSdeData();
		this.buildNodeParentMap(this.fullTreeData);
		this.initialLoading = false;
		if (this.dataPointUuid && uuidValidate(this.dataPointUuid)) {
			this.searchKey = this.dataPointUuid;
			this.selectNodeByDataPointUuid(this.dataPointUuid);
		}
	},

	methods: {
		...mapActions(useMainStore, ["showNotification"]),
		async getSdeData() {
			return this.$http
				.post(`/data-entry/download`, {
					uuids: [this.sdeUuid],
				})
				.then(async (response) => {
					if (!response.data || response.data.length === 0) return;

					const sdeData = response.data?.data?.sde;
					if (!sdeData || sdeData.length === 0) return;

					const extractionData = sdeData[0];

					this.dactylTree = extractionData.dactylTreeData.data;
					this.compositeKeyUtils = createCompositeKeyUtils(
						extractionData.wpdData.data.tags,
					);

					const studies = this.dactylTree?.studies;

					this.fullTreeData = this.transformSdeData(studies);
					this.flattenedTreeData = this.flattenTree(this.fullTreeData);

					const first = this.fullTreeData[0];
					this.treeViewData = [
						{
							...first,
							children: [],
						},
					];
				})
				.catch((error) => {
					if (error.response) {
						if (error.response.data.message) {
							this.showNotification(error.response.data);
						} else {
							this.showNotification(error.response.data.error);
						}
					}
				});
		},

		selectNode(node) {
			const selectedNode = this.findNodeById(this.flattenedTreeData, node.id);
			this.$emit("select:node", selectedNode);
			this.selectedNodeId = selectedNode?.id || null;
		},

		selectNodeByDataPointUuid(dataPointUuid) {
			const selectedNode = this.findNodeByDataPointId(
				this.flattenedTreeData,
				dataPointUuid,
			);

			if (!_.isEmpty(selectedNode)) {
				this.$emit("select:node", selectedNode);
				this.selectedNodeId = selectedNode.id;
			}
		},

		transformSdeData(nodes) {
			if (_.isEmpty(nodes)) return [];

			return nodes.map((node) => {
				const tagGroup = node.tagGroups[0];
				const isBranch = this.isBranchTagGroup(tagGroup.name);

				const dataPointChildren =
					node.data
						?.flatMap(
							(d) =>
								d.dataPoints?.map((dp) => ({
									id: `${node.compositeKey}|| ${d.tagGroupInstanceUuid} || ${
										dp.tagUuid
									} || ${uuidv4()}`,
									dataPointUuid: d.uuid,
									x: d.x,
									y: d.y,
									page: d.page,
									file: d.file ?? 0,
									instanceUuid: d.tagGroupInstanceUuid,
									tagUuid: dp.tagUuid,
									name: `${dp.tagName}: ${dp.value}`,
									tagName: dp.tagName,
									isDataPoint: true,
								})) || [],
						)
						.filter((dp) => !this.isBranchTag(dp.tagName)) || [];

				let tgName = "";
				if (isBranch) {
					tgName = `${node.data[0].dataPoints[0].value} (${tagGroup.name})`;
				} else {
					tgName = tagGroup.name;
				}
				return {
					id: `${node.compositeKey}|| ${tagGroup.instanceUuid}`,
					name: tgName,
					children: [
						...dataPointChildren,
						...this.transformSdeData(node.subComponents || []),
					],
				};
			});
		},

		findNodeById(nodes, id) {
			for (const node of nodes) {
				if (node.id === id) return node;
				if (node.children) {
					const found = this.findNodeById(node.children, id);
					if (found) return found;
				}
			}
			return null;
		},

		findNodeByDataPointId(nodes, id) {
			for (const node of nodes) {
				if (node.dataPointUuid === id) return node;
				if (node.children) {
					const found = this.findNodeByDataPointId(node.children, id);
					if (found) return found;
				}
			}
			return null;
		},

		loadChildren(item) {
			if (item.children?.length) return Promise.resolve([]);

			const fullNode = this.findNodeById(this.fullTreeData, item.id);
			if (fullNode?.children?.length) {
				item.children = fullNode.children.map((node) => ({
					...node,
					children: node.children?.length ? [] : undefined,
				}));
			}
			return Promise.resolve(item.children || []);
		},

		isBranchTagGroup(tgName) {
			return this.compositeKeyUtils.branchTagGroups.includes(tgName);
		},

		isBranchTag(tagName) {
			return this.compositeKeyUtils.branchTags.includes(tagName);
		},

		search(nodes, search) {
			const visibleNodeIds = new Set();
			const directMatches = new Set();
			const nodesWithMatchingChildren = new Set();
			const searchLower = search.toLowerCase();

			// First pass - identify direct matches
			for (const node of nodes) {
				if (
					(uuidValidate(this.dataPointUuid) &&
						node.dataPointUuid === searchLower) ||
					node.name.toLowerCase().includes(searchLower)
				) {
					directMatches.add(node.id);
				}
			}

			// Second pass - build parent-child relationships
			for (const node of nodes) {
				if (directMatches.has(node.id)) {
					// Direct matches should be visible
					visibleNodeIds.add(node.id);

					// Add all parents to visible set and mark them as having matching children
					if (node.parentPath) {
						for (const parentId of node.parentPath) {
							visibleNodeIds.add(parentId);
							nodesWithMatchingChildren.add(parentId);
						}
					}
				}
			}

			// Always make the root node visible
			const root = nodes[0];
			if (!visibleNodeIds.has(root.id)) {
				visibleNodeIds.add(root.id);
				directMatches.add(root.id);
			}

			return {
				visibleNodeIds,
				matchingInfo: {
					directMatches,
					nodesWithMatchingChildren,
				},
			};
		},

		async buildVisibleTree(
			fullTree,
			visibleNodes,
			matchingInfo,
			accumulator = [],
		) {
			for (const node of fullTree) {
				const isVisible = visibleNodes.has(node.id);
				const isDirectMatch = matchingInfo.directMatches.has(node.id);
				const hasMatchingChildren = matchingInfo.nodesWithMatchingChildren.has(
					node.id,
				);
				if (isVisible) {
					const newNode = {
						id: node.id,
						name: node.name,
						...(node.tagName && { tagName: node.tagName }),
						...(node.isDataPoint && { isDataPoint: true }),
					};
					if (node.children?.length) {
						if (hasMatchingChildren || !isDirectMatch) {
							newNode.children = [];
							await this.buildVisibleTree(
								node.children,
								visibleNodes,
								matchingInfo,
								newNode.children,
							);
						} else {
							newNode.children = node.children;
						}
					}
					accumulator.push(newNode);
					if (accumulator.length % 30 === 0) {
						await this.$nextTick();
						await new Promise((resolve) => requestAnimationFrame(resolve));
					}
				}
			}
		},

		flattenTree(nodes) {
			const flat = [];

			const walk = (nodeList, parentIds = []) => {
				for (const node of nodeList) {
					const parentPath = [...parentIds];
					flat.push({
						...node,
						parentPath,
					});

					if (node.children?.length) {
						walk(node.children, [...parentPath, node.id]);
					}
				}
			};

			walk(nodes);
			return flat;
		},

		buildNodeParentMap(nodes, parentId = null) {
			if (!nodes) return;

			nodes.forEach((node) => {
				if (parentId) {
					this.parentNodeMap[node.id] = parentId;
				}

				if (node.children && node.children.length) {
					this.buildNodeParentMap(node.children, node.id);
				}
			});
		},
	},
};
</script>

<style scoped lang="scss">
.selected-node {
	background-color: rgba(240, 157, 55, 0.18);
	border: 1px solid rgba(240, 157, 55, 0.55);
	border-radius: 6px;
	padding: 1px 8px 1px 8px;
	font-weight: 600;
}
</style>
